import React, { useState, useEffect, useCallback } from 'react';
import { AiOutlineArrowLeft, AiOutlineClose, AiOutlineInbox, AiOutlineCheck } from 'react-icons/ai';
import { TbTrash, TbRestore } from 'react-icons/tb';
import { apiUrl } from '../config/api';
import './RemovedPhotosPage.css';

// Admin-only recovery view for images that were removed (soft-tagged hidden, never
// actually deleted). Grouped client-wise: pick a client, see its removed photos, select
// one or many and restore them back into the normal project view.
const RemovedPhotosPage = ({ user }) => {
  const token = localStorage.getItem('token');
  // Admins and managers can review and restore removed photos.
  const isAdmin = !!(user?.isAdmin || user?.isManager);

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Multi-select for bulk restore within a client's photo grid
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [restoringIds, setRestoringIds] = useState(new Set());

  const loadHiddenImages = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(apiUrl('/api/scraper/hidden-images'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.detail || data.message || 'Failed to load removed photos'); return; }
      setClients(data.clients || []);
    } catch {
      setError('Failed to connect to server.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, token]);

  useEffect(() => { loadHiddenImages(); }, [loadHiddenImages]);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 3000);
    return () => clearTimeout(t);
  }, [message]);

  const openClient = (clientId) => {
    setSelectedClientId(clientId);
    setSelectedIds(new Set());
  };

  const closeClient = () => {
    setSelectedClientId(null);
    setSelectedIds(new Set());
  };

  const toggleSelected = (imageId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  };

  const selectAll = (imageIds) => setSelectedIds(new Set(imageIds));
  const clearSelection = () => setSelectedIds(new Set());

  // Restores one or many images in parallel — each is independent, so a failure on
  // one doesn't block the rest. Failed ones stay selected so they're easy to retry.
  const restoreImages = async (imageIds) => {
    if (imageIds.length === 0) return;
    setError('');
    setRestoringIds((prev) => new Set([...prev, ...imageIds]));

    const results = await Promise.all(
      imageIds.map(async (imageId) => {
        try {
          const res = await fetch(apiUrl('/api/scraper/unhide-image'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ imageId }),
          });
          return { imageId, ok: res.ok };
        } catch {
          return { imageId, ok: false };
        }
      })
    );

    const succeeded = results.filter((r) => r.ok).map((r) => r.imageId);
    const failed = results.filter((r) => !r.ok).map((r) => r.imageId);

    setRestoringIds((prev) => {
      const next = new Set(prev);
      imageIds.forEach((id) => next.delete(id));
      return next;
    });
    setSelectedIds((prev) => {
      const next = new Set(prev);
      succeeded.forEach((id) => next.delete(id));
      return next;
    });

    if (failed.length === 0) {
      setMessage(succeeded.length === 1 ? 'Photo restored' : `${succeeded.length} photos restored`);
    } else if (succeeded.length === 0) {
      setError(failed.length === 1 ? 'Failed to restore photo' : `Failed to restore ${failed.length} photos`);
    } else {
      setMessage(`${succeeded.length} restored`);
      setError(`${failed.length} failed to restore — still selected, try again`);
    }

    await loadHiddenImages();
  };

  if (!isAdmin) {
    return (
      <div className="rp-container">
        <div className="rp-denied">
          <AiOutlineClose size={22} />
          <p>You do not have permission to view removed photos.</p>
        </div>
      </div>
    );
  }

  const selectedClient = clients.find((c) => c.clientId === selectedClientId) || null;

  return (
    <div className="rp-container">
      <div className="rp-header">
        <h1 className="rp-title">Removed Photos</h1>
        <p className="rp-subtitle">
          Photos removed from a project are never deleted — restore any of them here.
        </p>
      </div>

      {message && <div className="rp-toast rp-toast--success">{message}</div>}
      {error && <div className="rp-toast rp-toast--error">{error}</div>}

      {loading ? (
        <p className="rp-loading">Loading…</p>
      ) : clients.length === 0 ? (
        <div className="rp-empty">
          <span className="rp-empty-icon"><AiOutlineInbox size={22} /></span>
          <p>No removed photos right now.</p>
        </div>
      ) : !selectedClient ? (
        <ul className="rp-client-list">
          {clients.map((c) => (
            <li key={c.clientId} className="rp-client-row" onClick={() => openClient(c.clientId)}>
              <div className="rp-client-icon"><TbTrash size={16} /></div>
              <div className="rp-client-text">
                <span className="rp-client-name">{c.clientName}</span>
                {c.clientAddress && <span className="rp-client-address">{c.clientAddress}</span>}
              </div>
              <span className="rp-client-count">{c.images.length} photo{c.images.length !== 1 ? 's' : ''}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rp-detail">
          <button type="button" className="rp-back-btn" onClick={closeClient}>
            <AiOutlineArrowLeft size={15} /> All clients
          </button>
          <div className="rp-detail-head">
            <p className="rp-detail-heading">{selectedClient.clientName}</p>
            {selectedIds.size === 0 ? (
              <button
                type="button"
                className="rp-select-all-btn"
                onClick={() => selectAll(selectedClient.images.map((img) => img.id))}
              >
                Select all
              </button>
            ) : (
              <button type="button" className="rp-select-all-btn" onClick={clearSelection}>
                Clear selection
              </button>
            )}
          </div>

          {/* Bulk-action bar — appears once at least one photo is selected */}
          {selectedIds.size > 0 && (() => {
            const anyRestoring = Array.from(selectedIds).some((id) => restoringIds.has(id));
            return (
              <div className="rp-bulk-bar">
                <span className="rp-bulk-count">{selectedIds.size} selected</span>
                <button
                  type="button"
                  className="rp-restore-btn rp-restore-btn--bulk"
                  onClick={() => restoreImages(Array.from(selectedIds))}
                  disabled={anyRestoring}
                >
                  <TbRestore size={14} />
                  {anyRestoring ? 'Restoring…' : 'Restore selected'}
                </button>
              </div>
            );
          })()}

          <div className="rp-photo-grid">
            {selectedClient.images.map((img) => {
              const isSelected = selectedIds.has(img.id);
              const isRestoring = restoringIds.has(img.id);
              return (
                <div key={img.id} className={`rp-photo-card${isSelected ? ' rp-photo-card--selected' : ''}`}>
                  <button
                    type="button"
                    className="rp-photo-thumb-btn"
                    onClick={() => toggleSelected(img.id)}
                    aria-pressed={isSelected}
                    aria-label={isSelected ? 'Deselect photo' : 'Select photo'}
                  >
                    {img.imageUrl ? (
                      <img src={img.imageUrl} alt="" className="rp-photo-thumb" />
                    ) : (
                      <span className="rp-photo-thumb rp-photo-thumb--video">Video</span>
                    )}
                    <span className={`rp-photo-checkbox${isSelected ? ' rp-photo-checkbox--checked' : ''}`}>
                      {isSelected && <AiOutlineCheck size={12} />}
                    </span>
                  </button>
                  <div className="rp-photo-meta">
                    <span className="rp-photo-date">
                      {img.hiddenAt ? `Removed ${new Date(img.hiddenAt).toLocaleDateString()}` : 'Removed'}
                    </span>
                    <button
                      type="button"
                      className="rp-restore-btn"
                      onClick={() => restoreImages([img.id])}
                      disabled={isRestoring}
                    >
                      <TbRestore size={14} />
                      {isRestoring ? 'Restoring…' : 'Restore'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default RemovedPhotosPage;
